import React, { useEffect, useState } from "react";
import DependentFilterBlock from "./components/DependentFilterBlock";
import { Grid } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import {
  ArrayInput,
  BooleanInput,
  FormDataConsumer,
  RadioButtonGroupInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
} from "react-admin";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import { NationalityType, WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import { settings } from "app";
import { ClassTypes, DisciplineCategory } from "utils/constants";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { validateFromToWeek, validateTimeEnd, validateTimeStart } from "./utils/utils";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import WeekSelectInput from "./components/Inputs/WeekSelectInput";
import RoomInput from "./components/Inputs/RoomInput";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useSearchParams } from "react-router-dom";
import { useFormContext, useWatch } from "react-hook-form";
import { tenants } from "utils/tenants";
import { useDisciplineCategory } from "./utils/useDisciplineCategory";

const CreateSaveForm = () => {
  const [defCourse, setDefCourse] = useState(null);
  const [semesterAndWeek, setSemesterAndWeek] = useState(null);

  const [searchParams] = useSearchParams();
  const { setValue, getValues } = useFormContext();

  const defaultValues = {
    specialityId: searchParams.get("specialityId") || "",
    curriculumId: searchParams.get("curriculumId") || "",
    instructionLanguageId: searchParams.get("instructionLanguageId") || "",
    academicYearId: searchParams.get("academicYearId") || "",
    studyTypeId: searchParams.get("studyTypeId") || "",
    academicYearSessionId: searchParams.get("academicYearSessionId") || "",
    disciplineId: searchParams.get("disciplineId") || "",
  };

  useEffect(() => {
    Object.entries(defaultValues).forEach(([key, value]) => {
      if (value && value !== "") {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          setValue(key, numValue, { shouldDirty: false, shouldValidate: true });
        }
      }
    });
  }, [setValue]);

  const curriculumId = useWatch({ name: "curriculumId" });
  const disciplineId = useWatch({ name: "disciplineId" });
  const disciplineCategory = useDisciplineCategory(disciplineId);

  useEffect(() => {
    if (curriculumId && disciplineId) {
      const fetchDefaultCourse = async () => {
        try {
          const { data } = await dataProvider.customRequest(
            studentRequests.getCurriculumDisciplineCoursesSelect(curriculumId, disciplineId),
            null,
            "GET",
          );
          setDefCourse(data[0] || null);
        } catch (err) {
          console.error(err);
        }
      };
      fetchDefaultCourse();
    }
  }, [curriculumId, disciplineId]);

  useEffect(() => {
    if (!curriculumId) return;
    const fetchSemesterWeekNumber = async () => {
      try {
        const { data } = await dataProvider.customRequest(
          studentRequests.getCurriculumStudyWeeksSelect(curriculumId),
          null,
          "GET",
        );
        setSemesterAndWeek(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchSemesterWeekNumber();
  }, [curriculumId]);

  // Clear nationality on every row when the discipline is not a foreign language -
  // the backend rejects a non-null nationality outside that category.
  useEffect(() => {
    if (disciplineCategory === null || disciplineCategory === DisciplineCategory.ForeignLanguage) return;
    (getValues("disciplineSchedules") ?? []).forEach((row, index) => {
      if (row?.nationality != null) {
        setValue(`disciplineSchedules.${index}.nationality`, null, { shouldDirty: true });
      }
    });
  }, [disciplineCategory, getValues, setValue]);

  return (
    <>
      <DependentFilterBlock defaultValues={defaultValues} />
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          md={12}
          sm={6}
          xs={12}
        >
          <ArrayInput
            source="disciplineSchedules"
            label="Разписания"
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableReordering
              disableClear
              sx={settings.style.iteratorWithoutGap}
            >
              <FormDataConsumer>
                {({ scopedFormData }) => {
                  const defaultFromWeek = scopedFormData?.fromWeek;
                  const defaultТоWeek = scopedFormData?.toWeek;
                  const classTypeId = scopedFormData?.classTypeId;
                  return (
                    <>
                      <Grid
                        container
                        spacing={2}
                      >
                        {disciplineCategory === DisciplineCategory.ForeignLanguage && (
                          <Grid
                            item
                            xs={12}
                            md={4}
                          >
                            <SelectInput
                              source="nationality"
                              choices={NationalityType}
                              label="Националност"
                              validate={[required()]}
                            />
                          </Grid>
                        )}
                        <Grid
                          item
                          xs={12}
                          md={4}
                        >
                          <ReferenceInput
                            source="classTypeId"
                            reference={administrationRequests.getRoomClassTypesSelect()}
                            filter={{ excludeIds: [ClassTypes.Csc] }}
                          >
                            <SelectInput
                              optionText="name"
                              label="Тип занятия"
                              validate={[required()]}
                            />
                          </ReferenceInput>
                        </Grid>
                        {disciplineId && curriculumId && (
                          <Grid
                            item
                            xs={12}
                            md={4}
                          >
                            <ReferenceInput
                              source="course"
                              reference={studentRequests.getCurriculumDisciplineCoursesSelect(
                                curriculumId,
                                disciplineId,
                              )}
                            >
                              <SelectInput
                                defaultValue={defCourse?.id}
                                label="Курс"
                                optionValue="id"
                                optionText="name"
                                fullWidth
                                validate={[required()]}
                              />
                            </ReferenceInput>
                          </Grid>
                        )}
                      </Grid>
                      <Grid
                        container
                        spacing={2}
                      >
                        <Grid
                          item
                          xs={12}
                          md={3}
                        >
                          <WeekSelectInput
                            semesterAndWeek={semesterAndWeek}
                            contextMode="create"
                            scopedFormData={scopedFormData}
                            source="fromWeek"
                            label="От седмица"
                            validate={[required(), () => validateFromToWeek(scopedFormData)]}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={3}
                        >
                          <WeekSelectInput
                            semesterAndWeek={semesterAndWeek}
                            contextMode="create"
                            scopedFormData={scopedFormData}
                            source="toWeek"
                            label="До седмица"
                            validate={[required(), () => validateFromToWeek(scopedFormData)]}
                          />
                        </Grid>
                      </Grid>
                      {disciplineId && (
                        <ArrayInput
                          source="professors"
                          label="Преподаватели"
                          defaultValue={[{}]}
                        >
                          <SimpleFormIterator
                            disableReordering
                            disableClear
                            sx={settings.style.iteratorWithoutGap}
                          >
                            <Grid
                              container
                              spacing={2}
                            >
                              <Grid
                                item
                                xs={12}
                                md={4}
                              >
                                <ProfessorInput
                                  source="employeeId"
                                  label="Преподавател"
                                  fullWidth
                                  validate={[required()]}
                                  filters={{ DisciplineId: disciplineId }}
                                />
                              </Grid>
                              <FormDataConsumer>
                                {({ scopedFormData }) => (
                                  <>
                                    <Grid
                                      item
                                      xs={12}
                                      md={4}
                                    >
                                      <WeekSelectInput
                                        semesterAndWeek={semesterAndWeek}
                                        contextMode="create"
                                        isChild={true}
                                        defaultValue={defaultFromWeek}
                                        source="fromWeek"
                                        label="От седмица"
                                        validate={[required(), () => validateFromToWeek(scopedFormData)]}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      md={4}
                                    >
                                      <WeekSelectInput
                                        semesterAndWeek={semesterAndWeek}
                                        contextMode="create"
                                        isChild={true}
                                        defaultValue={defaultТоWeek}
                                        source="toWeek"
                                        label="До седмица"
                                        validate={[required(), () => validateFromToWeek(scopedFormData)]}
                                      />
                                    </Grid>
                                  </>
                                )}
                              </FormDataConsumer>
                            </Grid>
                          </SimpleFormIterator>
                        </ArrayInput>
                      )}
                      <ArrayInput
                        source="weeklySchedule"
                        label="Седмичен график"
                        defaultValue={[{}]}
                      >
                        <SimpleFormIterator
                          disableReordering
                          disableClear
                          sx={settings.style.iteratorWithoutGap}
                        >
                          {curriculumId && disciplineId && (
                            <Grid
                              item
                              xs={12}
                            >
                              <ReferenceInput
                                source="curriculumModuleId"
                                reference={studentRequests.getCurriculumDisciplineModulesSelect(
                                  curriculumId,
                                  disciplineId,
                                )}
                              >
                                <SelectInput
                                  optionText="name"
                                  label="Модул"
                                />
                              </ReferenceInput>
                            </Grid>
                          )}

                          <Grid
                            item
                            xs={12}
                          >
                            <RadioButtonGroupInput
                              label="Тип седмица"
                              source="weekType"
                              choices={WeekTypeChoices}
                              validate={required()}
                            />
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={4}
                          >
                            <SelectInput
                              source="weekDay"
                              label="Ден от седмицата"
                              choices={WeekDaysChoices}
                              optionValue="id"
                              optionText="name"
                              validate={[required()]}
                            />
                          </Grid>
                          <RoomInput
                            classTypeId={classTypeId}
                            disciplineId={disciplineId}
                          />
                          <Grid
                            container
                            spacing={2}
                            sx={{ mb: 3 }}
                          >
                            <Grid
                              item
                              xs={12}
                              md={4}
                            >
                              <FormDataConsumer>
                                {({ scopedFormData }) => (
                                  <>
                                    <CustomTimeOnlyInput
                                      source="startTime"
                                      label="Начален час"
                                      validate={() => validateTimeStart(scopedFormData)}
                                      required
                                    />
                                  </>
                                )}
                              </FormDataConsumer>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              md={4}
                            >
                              <FormDataConsumer>
                                {({ scopedFormData }) => (
                                  <>
                                    <CustomTimeOnlyInput
                                      source="endTime"
                                      label="Краен час"
                                      validate={() => validateTimeEnd(scopedFormData)}
                                      required
                                    />
                                  </>
                                )}
                              </FormDataConsumer>
                            </Grid>
                          </Grid>
                          <Grid
                            container
                            spacing={2}
                            sx={{ mb: 3 }}
                          >
                            <Grid
                              item
                              xs={12}
                              md={4}
                            >
                              <StudyGroupField
                                label=" Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Оставете празно ако разписанието се отнася за всички
        групи!"
                              />
                            </Grid>
                            {scopedFormData?.classTypeId === ClassTypes.Exercies && settings.tenant === tenants.MU && (
                              <Grid
                                item
                                xs={12}
                                md={4}
                              >
                                <BooleanInput
                                  source="splitAcrossGroups"
                                  label="Раздели по групи"
                                />
                              </Grid>
                            )}
                          </Grid>
                        </SimpleFormIterator>
                      </ArrayInput>
                    </>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
    </>
  );
};

export default CreateSaveForm;
